import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MessageService} from 'primeng/api';
import {NgForm} from '@angular/forms';
import {Company} from '../../types/company';
import {identityTypes} from '../../types/identity-type';
import {Shareholder} from '../../types/shareholder';
import {Attachment} from "../../types/attachment";
import {HttpEventType} from "@angular/common/http";
import {Subscription} from "rxjs";
import {FormService} from "../../services/form.service";
import {AttachmentType} from "../../types/attachment-type";
import {FileUpload} from "primeng/fileupload";
import {Constants} from "../../types/constants";

type AttachmentInternal = {
  attachment ?: Attachment,
  subscription ?: Subscription
}

@Component({
  selector: 'shareholders',
  templateUrl: './shareholders.component.html',
  styleUrls: ['./shareholders.component.css'],
  providers: []
})
export class ShareholdersComponent implements OnInit {
  @Input() docId !: number;
  @Input() company !: Company;
  @Input() shareholders !: Shareholder[];
  @Output() shareholdersChange = new EventEmitter<Shareholder[]>();
  @Output() onlyPeselsChange = new EventEmitter<boolean>();
  
  @ViewChild('addShareholderForm') addShareholderForm!: NgForm;
  @ViewChild('krkForm') krkFileUpload?: FileUpload;
  
  displayShareholderAdding: boolean = false;
  noPesel : boolean = false;
  shareholder : Shareholder = { type: "OF_P_1", boardMember: true, personalData: {}};
  identityTypes = identityTypes;
  identityTypesMap : Map<string, string> = new Map([["PASZPORT","Paszport"], ["DOWOD","Dowód"], ["INNY_DOK","Inny dokument"]]);
  editIndex : number = -1;
  krkFile ?: AttachmentInternal;
  krkError : boolean = false;
  krkMap : Map<number, Attachment> = new Map();
  shareholdersFormDirty : boolean = false;
  shareholdersNamesInvalid : boolean = false;
  shareholdersEmpty : boolean = false;
  firstNameMask : string = "";
  firstNamePlaceholder : string = "";
  surnameMask : string = "";
  surnamePlaceholder : string = "";
  peselMask : string = "";
  peselPlaceholder : string = "";
  alreadyFilled: boolean = false; // currently editing shareholder was already properly filled
  peselsOnly : boolean = true; // all partners have pesels
  identityFilter : RegExp = /^[\w\-\s]+$/;
    
  constructor(
    private messageService: MessageService,
    private formService: FormService) { }

  ngOnInit(): void {
    this.shareholder = { type: "OF_P_1", boardMember: true, personalData: {}};
    this.displayShareholderAdding = false;
    this.shareholdersFormDirty = false;
    this.shareholdersEmpty = this.shareholders.length == 0;
    this.noPesel = false;
    this.krkFile = undefined;
    this.krkMap.clear();
    this.alreadyFilled = false;
    this.recalculateNamesValidity();
    this.peselsOnly = true;
    this.recalculatePeselsOnly();
    this.onlyPeselsChange.emit(this.peselsOnly);
  }

  noNipChanged(checked: boolean) : void {
    if (checked) {
      this.shareholder.nip = undefined;
    }
  }

  saveShareholder() {
    this.addShareholderForm.form.markAllAsTouched();
    let krkId = this.krkFile?.attachment?.id;
    if (krkId == undefined || krkId == 0)
      this.krkError = true;
    if (this.addShareholderForm.invalid || krkId == undefined || krkId == 0) {
      this.messageService.add({
        severity:'warn',
        detail:'Formularz wypełniony nieprawidłowo. Popraw błędy zaznaczone na czerwono'  }
      );
      return;
    }
    if (this.shareholder.nip && this.shareholder.nip == this.company.nip) {
      this.messageService.add({
        severity:'warn',
        detail:'NIP członka zarządu nie może być taki sam jak NIP spółki.'  }
      );
      return;
    }
    for (let sh of this.shareholders) {
      if (this.shareholder.nip && this.shareholder.nip == sh.nip && this.shareholder.nip != this.shareholders[this.editIndex].nip) {
        this.messageService.add({
            severity: 'warn',
            detail: 'NIP występuje już u innego członka zarządu.'
          }
        );
        return;
      }
      if (this.shareholder.personalData?.pesel == sh.personalData?.pesel && this.shareholder.personalData?.pesel != this.shareholders[this.editIndex].personalData?.pesel) {
        this.messageService.add({
            severity: 'warn',
            detail: 'PESEL występuje już u innego członka zarządu.'
          }
        );
        return;
      }
    }

    this.shareholders[this.editIndex] = this.shareholder;
    if (this.shareholdersNamesInvalid)
      this.recalculateNamesValidity();
    this.displayShareholderAdding = false;
    this.shareholdersChange.emit(this.shareholders);
  }
  
  onShareholderRowEdit(shareholder: Shareholder, index: number) {
    let personalData = {...shareholder.personalData};
    this.firstNameMask = personalData.firstname?.replace(/(?<=\S)\S/g, 'a') ?? "";
    this.surnameMask = personalData.surname?.replace(/(?<=\S)\S/g, 'a') ?? "";
    this.peselMask = personalData.pesel?.replace(/(?<=\S)\S/g, '9') ?? "";
    this.firstNamePlaceholder = personalData.firstname!;
    this.surnamePlaceholder = personalData.surname!;
    this.peselPlaceholder = personalData.pesel!;
    // usunięcie gwiazdek z imienia i nazwiska
    if (personalData.firstname?.includes('*'))
      personalData.firstname = undefined;
    if (personalData.surname?.includes('*'))
      personalData.surname = undefined;
    if (personalData.pesel?.includes('*')) {
      personalData.pesel = undefined;
      this.noPesel = false;
    } else
      this.noPesel = !personalData.pesel;
    this.shareholder = {...shareholder};
    this.shareholder.personalData = personalData;

    if (personalData.krkId != null) {
      this.krkFile = {attachment: this.krkMap.get(personalData.krkId)};
      this.alreadyFilled = true;
    } else {
      if (this.krkFileUpload) {
        this.krkFileUpload.clear();
        this.krkFileUpload.uploadedFileCount = 0;
        this.krkFileUpload.disabled = false;
      }
      this.alreadyFilled = false;
      this.shareholder.noNip = false; // user must click checkbox if there is no nip
    }

    this.editIndex = index;
    this.displayShareholderAdding = true;
  }

  uploadHandler(event: any, fileUpload: FileUpload) {
    let file = event.files[0];
    if (!file)
      return;
    if (this.editIndex >= 0 && this.krkFile && this.krkFile.attachment)
      this.krkMap.delete(this.krkFile.attachment.id);

    if (!this.shareholder.personalData?.firstname) {
      this.messageService.add({
        severity:'warn',
        detail: "Najpierw podaj imię" }
      );
      fileUpload.clear();
      return;
    }

    if (!this.shareholder.personalData?.surname) {
      this.messageService.add({
        severity:'warn',
        detail: "Najpierw podaj nazwisko" }
      );
      fileUpload.clear();
      return;
    }

    let userId = this.shareholder.personalData?.pesel ? this.shareholder.personalData.pesel : this.shareholder.personalData?.identityValue;
    if (!userId) {
      this.messageService.add({
        severity:'warn',
        detail: "Najpierw podaj " + (this.noPesel ? "rodzaj i numer dokumentu" : "numer pesel")}
      );
      fileUpload.clear();
      return;
    }

    let attachment: Attachment = {name: file.name, id: 0};
    this.krkFile = {attachment: attachment};
    fileUpload.disabled = true;
    fileUpload.progress = 0;
    this.krkFile.subscription = this.formService.uploadFile(file, this.docId, AttachmentType.KRK,
                this.shareholder.personalData?.firstname,
                this.shareholder.personalData?.surname,
                this.shareholder.personalData?.pesel)
      .subscribe(
        event => {
          if (event.type === HttpEventType.UploadProgress) {
            fileUpload.progress = Math.round(100 * (event.loaded / event.total));
            fileUpload.cd.markForCheck();
          }
          if (event.type === HttpEventType.Response) {
            let krkId =  Number(event.body.message);
            attachment.id = krkId;
            this.shareholder.personalData!.krkId = krkId;
            this.krkMap.set(krkId, attachment);
            this.krkError = false;
          }
        },
        error => {
          this.messageService.add({
            severity: (error.status == 400 && error.error.message != Constants.GenericError) ? 'warn' : 'error',
            detail: error.error.message ?? Constants.GenericError}
          );
          this.krkFile = undefined;
          this.krkError = true;
          fileUpload.clear();
          fileUpload.uploadedFileCount = 0;
          fileUpload.disabled = false;
        }
      );
  }
  fileRemove(fileUpload: FileUpload) {
    if (this.krkFile!.subscription != undefined) {
      this.krkFile!.subscription.unsubscribe();
      this.krkFile!.subscription = undefined;
    }
    if (this.editIndex == -1)
      this.krkMap.delete(this.krkFile!.attachment!.id);
    this.krkFile = undefined;
    this.krkError = true;
    this.shareholder.personalData!.krkId = undefined;
    fileUpload.uploadedFileCount = 0;
    fileUpload.progress = 0;
    fileUpload.disabled = false;
  }

  onDialogClosed() {
    if (this.krkFile && this.krkFile.subscription != undefined) {
      this.krkFile.subscription.unsubscribe();
      this.krkFile.subscription = undefined;
    }
    this.krkFile = undefined;
  }

  setFormDirty(dirty : boolean) {
    this.shareholdersFormDirty = dirty;
  }

  editFormInvalid() : boolean {
    if (this.shareholdersEmpty || (this.shareholdersFormDirty && this.formInvalid())) {
      return true;
    }
    return false;
  }

  formInvalid() : boolean {
    return this.shareholdersEmpty || this.shareholdersNamesInvalid;
  }

  // przeliczenie poprawności imion i nazwisk, czy nie zawierają gwiazdek
  private recalculateNamesValidity() {
    for (let shareholder of this.shareholders) {
      if (shareholder.personalData?.firstname?.includes('*')
        || shareholder.personalData?.surname?.includes('*')
        || shareholder.personalData?.pesel?.includes('*')) {
        this.shareholdersNamesInvalid = true;
        return;
      }
    }
    this.shareholdersNamesInvalid = false;
  }

  private recalculatePeselsOnly() {
    for (let shareholder of this.shareholders) {
      if (!shareholder.personalData?.pesel) {
        this.peselsOnly = false;
      }
    }
  }
}
